/**
 * The classic tender overview's column list (the "all offers" grid).
 *
 * Exported so the v2 parity registry (app/src/app/v2/parity/parity-registry.ts) reads the
 * columns from classic instead of keeping a copy (Plan 7b Ruling F-34). Formula columns are
 * appended at runtime by the component (one per active company formula).
 */
export interface HeaderDef {
    formId: string;
    label: string;
    id: string;
    order: boolean;
    index: number;
    freezable?: boolean;
    width?: number;
}

export function buildFeeHeaders(): HeaderDef[] {
    const out: HeaderDef[] = [];
    let idx = 27;
    for (let i = 1; i <= 12; i++) {
        out.push({ formId: `fee_${i}`, label: `Fee ${i}`, id: `fee_${i}`, order: false, index: idx++ });
        out.push({ formId: `fee_${i}_price`, label: 'Price', id: `fee_${i}_price`, order: false, index: idx++ });
        out.push({ formId: `fee_${i}_unit`, label: 'Unit', id: `fee_${i}_unit`, order: false, index: idx++ });
    }
    return out;
}

export function overviewHeaders(): HeaderDef[] {
    return [
        { formId: 'iata', label: 'IATA', id: 'iata', order: true, index: 1, freezable: true, width: 80 },
        { formId: 'location_company', label: 'Company', id: 'location_company', order: true, index: 100, freezable: true, width: 200 },
        { formId: 'contract_period', label: 'Contract Period', id: 'contract_period', order: true, index: 2 },
        { formId: 'yearly_volume', label: 'Yearly Volume', id: 'yearly_volume', order: true, index: 3, freezable: true, width: 130 },
        { formId: 'monthly_volume', label: 'Monthly Volume', id: 'monthly_volume', order: true, index: 4, freezable: true, width: 130 },
        { formId: 'months_of_service', label: 'Months of Service', id: 'months_of_service', order: true, index: 5 },
        { formId: 'fuel_type', label: 'Fuel Type', id: 'fuel_type', order: true, index: 50, freezable: true, width: 110 },
        { formId: 'flight_type', label: 'Flight Type', id: 'flight_type', order: true, index: 51, freezable: true, width: 110 },
        { formId: 'delivery_point', label: 'Delivery Point', id: 'delivery_point', order: true, index: 52, freezable: true, width: 130 },
        { formId: 'supplier', label: 'Supplier', id: 'supplier', order: true, index: 6, freezable: true, width: 200 },
        { formId: 'alias', label: 'Alias', id: 'alias', order: true, index: 7 },
        { formId: 'round', label: 'Round', id: 'round', order: true, index: 8, freezable: true, width: 80 },
        { formId: 'location_ranking', label: 'Rank', id: 'location_ranking', order: true, index: 8.5, freezable: true, width: 70 },
        { formId: 'offer', label: 'Offer', id: 'offer', order: true, index: 9, freezable: true, width: 140 },
        { formId: 'status', label: 'Status', id: 'status', order: true, index: 10, freezable: true, width: 120 },
        { formId: 'intoplane', label: 'Intoplane', id: 'intoplane', order: true, index: 11 },
        { formId: 'fuel_availability', label: 'Fuel Availability', id: 'fuel_availability', order: true, index: 12 },
        { formId: 'differential', label: 'Differential', id: 'differential', order: true, index: 13 },
        { formId: 'diff_currency_units', label: 'Currency / Units', id: 'diff_currency_units', order: true, index: 14 },
        { formId: 'base', label: 'Base', id: 'base', order: true, index: 15 },
        { formId: 'bate', label: 'Bate', id: 'bate', order: true, index: 16 },
        { formId: 'averaging_method', label: 'Averaging Method', id: 'averaging_method', order: true, index: 17 },
        { formId: 'averaging_offset', label: 'Averaging Offset', id: 'averaging_offset', order: true, index: 18 },
        { formId: 'update', label: 'Update', id: 'update', order: true, index: 19 },
        { formId: 'index_market_value', label: 'Index / Market Price Value', id: 'index_market_value', order: true, index: 20 },
        { formId: 'index_market_unit', label: 'Unit', id: 'index_market_unit', order: true, index: 21 },
        { formId: 'index_market_currency', label: 'Currency', id: 'index_market_currency', order: true, index: 22 },
        { formId: 'per_usage_based_fees', label: 'Per Usage Based Fees', id: 'per_usage_based_fees', order: true, index: 23 },
        { formId: 'per_usage_currency', label: 'Currency', id: 'per_usage_currency', order: false, index: 24 },
        { formId: 'av_uplift', label: 'Av. Uplift', id: 'av_uplift', order: true, index: 25 },
        { formId: 'fees_1_12', label: 'All Fees', id: 'all_fees', order: true, index: 26 },
        ...buildFeeHeaders(),
        { formId: 'total_fees', label: 'Total Fees', id: 'total_fees', order: true, index: 63 },
        { formId: 'total_fees_unit', label: 'Unit', id: 'total_fees_unit', order: false, index: 64 },
        { formId: 'gap_vs_prev', label: 'Gap vs prev.', id: 'gap_vs_prev', order: true, index: 69 },
        { formId: 'gap_vs_prev_unit', label: 'Unit', id: 'gap_vs_prev_unit', order: false, index: 70 },
        { formId: 'gap_total', label: 'Gap Total', id: 'gap_total', order: true, index: 71 },
        { formId: 'contract_total', label: 'Contract Total', id: 'contract_total', order: true, index: 71.5 },
        { formId: 'selected', label: 'Selected', id: 'selected', order: false, index: 72 },
        { formId: 'award_percentage', label: 'Award Percentage', id: 'award_percentage', order: false, index: 73 },
        { formId: 'award_gap_total', label: 'Award Gap Total', id: 'award_gap_total', order: true, index: 74 },
    ];
}
